import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Sparkles, ArrowLeft, CalendarIcon, Save, 
  LogOut, Calculator, Users, Loader2 
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

export const SettingsPage = () => {
  const { user, updateProfile, logout } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    full_name: user?.full_name || '',
    birth_date: user?.birth_date ? parseISO(user.birth_date) : null,
    bio: user?.bio || '',
    looking_for: user?.looking_for || 'connection'
  });
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await updateProfile({
        ...formData,
        birth_date: formData.birth_date ? format(formData.birth_date, 'yyyy-MM-dd') : undefined
      });
      toast.success('Profile updated successfully!');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <header className="bg-white/60 backdrop-blur-xl border-b border-[#E8E2D2] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center gap-3" data-testid="settings-logo">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#F3E5AB] to-[#D4AF37] flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading text-2xl text-[#2D2A26]">Intuitive Matchmaker</span>
            </Link>
            
            <nav className="flex items-center gap-6">
              <Link 
                to="/dashboard" 
                className="text-[#4A453E] hover:text-[#D4AF37] transition-colors font-medium hidden md:inline-flex"
                data-testid="nav-dashboard"
              >
                Dashboard
              </Link>
              <Link 
                to="/matches" 
                className="text-[#4A453E] hover:text-[#D4AF37] transition-colors font-medium hidden md:inline-flex items-center gap-2"
                data-testid="nav-matches"
              >
                <Users className="w-4 h-4" />
                Matches
              </Link>
              <Link 
                to="/calculator" 
                className="text-[#4A453E] hover:text-[#D4AF37] transition-colors font-medium hidden md:inline-flex items-center gap-2"
                data-testid="nav-calculator"
              >
                <Calculator className="w-4 h-4" />
              </Link>
              <button 
                onClick={handleLogout}
                className="text-[#8C857B] hover:text-[#D4AF37] transition-colors"
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </nav>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 md:px-12 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Link 
            to="/dashboard" 
            className="inline-flex items-center gap-2 text-[#8C857B] hover:text-[#D4AF37] mb-8 transition-colors"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to Dashboard</span>
          </Link>

          <h1 className="font-heading text-4xl text-[#2D2A26] font-light mb-2">Settings</h1>
          <p className="text-[#8C857B] mb-8">Update your cosmic profile</p>

          <div className="ethereal-card rounded-3xl p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="full_name" className="text-[#4A453E]">Full Birth Name</Label>
                <Input
                  id="full_name"
                  value={formData.full_name}
                  onChange={(e) => handleChange('full_name', e.target.value)}
                  placeholder="Your full birth name"
                  className="bg-white/50 border-[#E8E2D2] focus:border-[#D4AF37] focus:ring-[#D4AF37] rounded-xl h-12"
                  data-testid="settings-name-input"
                />
                <p className="text-xs text-[#8C857B]">
                  Changing your name will recalculate your numerology numbers
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-[#4A453E]">Birth Date</Label>
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full h-12 justify-start text-left font-normal bg-white/50 border-[#E8E2D2] hover:border-[#D4AF37] hover:bg-white/70 rounded-xl"
                      data-testid="settings-birthdate-trigger"
                    >
                      <CalendarIcon className="mr-3 h-4 w-4 text-[#D4AF37]" />
                      {formData.birth_date ? (
                        <span className="text-[#2D2A26]">{format(formData.birth_date, 'MMMM d, yyyy')}</span>
                      ) : (
                        <span className="text-[#8C857B]">Select your birth date</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-white border-[#E8E2D2]" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.birth_date}
                      onSelect={(date) => {
                        handleChange('birth_date', date);
                        setCalendarOpen(false);
                      }}
                      disabled={(date) => date > new Date() || date < new Date('1920-01-01')}
                      captionLayout="dropdown-buttons"
                      fromYear={1920}
                      toYear={new Date().getFullYear()}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label className="text-[#4A453E]">Looking For</Label>
                <Select 
                  value={formData.looking_for} 
                  onValueChange={(value) => handleChange('looking_for', value)}
                >
                  <SelectTrigger 
                    className="h-12 bg-white/50 border-[#E8E2D2] focus:border-[#D4AF37] rounded-xl"
                    data-testid="settings-looking-for-select"
                  >
                    <SelectValue placeholder="What are you seeking?" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#E8E2D2]">
                    <SelectItem value="connection">Spiritual Connection</SelectItem>
                    <SelectItem value="love">Romantic Love</SelectItem>
                    <SelectItem value="friendship">Kindred Spirits</SelectItem>
                    <SelectItem value="guidance">Life Guidance</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="bio" className="text-[#4A453E]">About You</Label>
                <Textarea
                  id="bio"
                  value={formData.bio}
                  onChange={(e) => handleChange('bio', e.target.value)}
                  placeholder="Share a bit about your spiritual journey..."
                  className="bg-white/50 border-[#E8E2D2] focus:border-[#D4AF37] focus:ring-[#D4AF37] rounded-xl resize-none"
                  rows={4}
                  data-testid="settings-bio-input"
                />
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full h-12 rounded-full btn-gold text-white font-medium"
                data-testid="settings-save-btn"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </form>
          </div>

          {/* Current Numerology */}
          {user?.numerology && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="ethereal-card rounded-2xl p-6 mt-8"
            >
              <h3 className="font-heading text-xl text-[#2D2A26] mb-4">Your Current Numbers</h3>
              <div className="grid grid-cols-4 gap-4 text-center">
                {[
                  { label: 'Life Path', value: user.numerology.life_path },
                  { label: 'Expression', value: user.numerology.expression },
                  { label: 'Soul Urge', value: user.numerology.soul_urge },
                  { label: 'Personality', value: user.numerology.personality },
                ].map((item) => (
                  <div key={item.label}>
                    <div className="number-circle w-12 h-12 text-lg mx-auto mb-2">
                      {item.value}
                    </div>
                    <p className="text-[#8C857B] text-xs">{item.label}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </motion.div>
      </main>
    </div>
  );
};

export default SettingsPage;
